import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface MenuTag {
    color: string; // background color
    value: string;
}

export interface MenuChildrenItem {
    route: string;
    name: string;
    type: 'link' | 'sub' | 'extLink' | 'extTabLink';
    children?: MenuChildrenItem[];
}

export interface Menu {
    route: string;
    name: string;
    type: 'link' | 'sub' | 'extLink' | 'extTabLink';
    icon: string;
    label?: MenuTag;
    badge?: MenuTag;
    children?: MenuChildrenItem[];
}

@Injectable({
    providedIn: 'root',
})
export class MenuService {
    private readonly menu$: BehaviorSubject<Menu[]> = new BehaviorSubject<Menu[]>([]);

    getAll(): Observable<Menu[]> {
        return this.menu$.asObservable();
    }

    set(menu: Menu[]): Observable<Menu[]> {
        this.menu$.next(menu);
        return this.menu$.asObservable();
    }

    add(menu: Menu) {
        const tmpMenu = this.menu$.value;
        tmpMenu.push(menu);
        this.menu$.next(tmpMenu);
    }

    reset() {
        this.menu$.next([]);
    }

    getMenuItemName(routeArr: string[]): string {
        return this.getMenuLevel(routeArr)[routeArr.length - 1];
    }

    private isLeafItem(item: MenuChildrenItem): boolean {
        const cond0 = item.route === undefined;
        const cond1 = item.children === undefined;
        const cond2 = !cond1 && item.children.length === 0;
        return cond0 || cond1 || cond2;
    }

    // Deep clone object could be jsonized
    private deepClone(obj: any): any {
        return JSON.parse(JSON.stringify(obj));
    }

    // Whether two objects could be jsonized equal
    private isJsonObjEqual(obj0: any, obj1: any): boolean {
        return JSON.stringify(obj0) === JSON.stringify(obj1);
    }

    // Whether routeArr equals realRouteArr (after remove empty route element)
    private isRouteEqual(routeArr: Array<string>, realRouteArr: Array<string>): boolean {
        realRouteArr = this.deepClone(realRouteArr);
        realRouteArr = realRouteArr.filter(r => r !== '');
        return this.isJsonObjEqual(routeArr, realRouteArr);
    }

    getMenuLevel(routeArr: string[]): string[] {
        let tmpArr = [];
        this.menu$.value.forEach(item => {
            // breadth first traverse modified
            let unhandledLayer = [{ item, parentNamePathList: [], realRouteArr: [] }];
            while (unhandledLayer.length > 0) {
                let nextUnhandledLayer = [];
                for (const ele of unhandledLayer) {
                    const eachItem = ele.item;
                    const currentNamePathList = this.deepClone(ele.parentNamePathList).concat(eachItem.name);
                    const currentRealRouteArr = this.deepClone(ele.realRouteArr).concat(eachItem.route);
                    // compare the full Array for expandable
                    if (this.isRouteEqual(routeArr, currentRealRouteArr)) {
                        tmpArr = currentNamePathList;
                        break;
                    }
                    if (!this.isLeafItem(eachItem)) {
                        const wrappedChildren = eachItem.children.map(child => ({
                            item: child,
                            parentNamePathList: currentNamePathList,
                            realRouteArr: currentRealRouteArr,
                        }));
                        nextUnhandledLayer = nextUnhandledLayer.concat(wrappedChildren);
                    }
                }
                unhandledLayer = nextUnhandledLayer;
            }
        });
        return tmpArr;
    }

    /** Menu for translation */

    recursMenuForTranslation(menu: Menu[] | MenuChildrenItem[], namespace: string) {
        menu.forEach(menuItem => {
            menuItem.name = `${namespace}.${menuItem.name}`;
            if (menuItem.children && menuItem.children.length > 0) {
                this.recursMenuForTranslation(menuItem.children, menuItem.name);
            }
        });
    }
}
